import { capitalize } from "~/lib/text-helpers"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Input } from "~/react/ui/input"
import { Radio } from "~/react/ui/radio"
import { Checkbox } from "~/react/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuHeader,
  DropdownMenuTitle,
  DropdownMenuItem,
  DropdownMenuInput,
  DropdownMenuSubmit,
} from "~/react/ui/dropdown-menu"

// Shared filter panel types
export type HealthFilterState = {
  healthFrom: number | null
  healthTo: number | null
  healthIsEmpty: boolean
  healthHasAnyValue?: boolean
}

// Health Filter Panel Component
type HealthPanelProps = {
  tempFilters: HealthFilterState
  setTempFilters: (filters: HealthFilterState) => void
  onBack: () => void
  onApply: () => void
  idSuffix?: string
  titleKey?: string
  hideHeader?: boolean
}

export const HealthFilterPanel = ({
  tempFilters,
  setTempFilters,
  onBack,
  onApply,
  idSuffix = "",
  titleKey = "js.email_accounts_center.columns.health",
  hideHeader = false,
}: HealthPanelProps) => {
  const suffix = idSuffix ? `-${idSuffix}` : ""

  return (
    <>
      {!hideHeader && (
        <DropdownMenuHeader>
          <Button variant="ghost" size="sm" iconOnly type="button" onClick={onBack}>
            <Icon icon="arrow-left" />
          </Button>
          <DropdownMenuTitle>{i18n.t(titleKey)}</DropdownMenuTitle>
        </DropdownMenuHeader>
      )}
      <DropdownMenuItem>
        <Radio
          id={`filter-health-within${suffix}`}
          name={`health-filter${suffix}`}
          checked={(tempFilters.healthFrom !== null || tempFilters.healthTo !== null) && !tempFilters.healthIsEmpty}
          onChange={() =>
            setTempFilters({
              ...tempFilters,
              healthFrom: tempFilters.healthFrom ?? 0,
              healthTo: tempFilters.healthTo ?? 100,
              healthIsEmpty: false,
              healthHasAnyValue: false,
            })
          }
        />
        <label htmlFor={`filter-health-within${suffix}`}>{capitalize(i18n.t("js.filters_options.within"))}</label>
        {(tempFilters.healthFrom !== null || tempFilters.healthTo !== null) && !tempFilters.healthIsEmpty && (
          <>
            <DropdownMenuInput>
              <label htmlFor={`filter-health-from${suffix}`}>{i18n.t("js.from")}</label>
              <Input
                id={`filter-health-from${suffix}`}
                type="number"
                min="0"
                max="100"
                size="sm"
                fullWidth
                value={(tempFilters.healthFrom ?? "") as string | number}
                onChange={(e) => {
                  const parsed = e.target.value ? parseInt(e.target.value, 10) : null
                  const healthFrom = parsed !== null && Number.isNaN(parsed) ? null : parsed
                  setTempFilters({
                    ...tempFilters,
                    healthFrom,
                    healthHasAnyValue: false,
                  })
                }}
              />
            </DropdownMenuInput>
            <DropdownMenuInput>
              <label htmlFor={`filter-health-to${suffix}`}>{i18n.t("js.to")}</label>
              <Input
                id={`filter-health-to${suffix}`}
                type="number"
                min="0"
                max="100"
                size="sm"
                fullWidth
                value={(tempFilters.healthTo ?? "") as string | number}
                onChange={(e) => {
                  const parsed = e.target.value ? parseInt(e.target.value, 10) : null
                  const healthTo = parsed !== null && Number.isNaN(parsed) ? null : parsed
                  setTempFilters({
                    ...tempFilters,
                    healthTo,
                    healthHasAnyValue: false,
                  })
                }}
              />
            </DropdownMenuInput>
          </>
        )}
      </DropdownMenuItem>
      <DropdownMenuItem>
        <Radio
          id={`filter-health-any${suffix}`}
          name={`health-filter${suffix}`}
          checked={!!tempFilters.healthHasAnyValue}
          onChange={() =>
            setTempFilters({
              ...tempFilters,
              healthFrom: null,
              healthTo: null,
              healthIsEmpty: false,
              healthHasAnyValue: true,
            })
          }
        />
        <label htmlFor={`filter-health-any${suffix}`}>{capitalize(i18n.t("js.filters_options.has_any_value"))}</label>
      </DropdownMenuItem>
      <DropdownMenuItem>
        <Radio
          id={`filter-health-empty${suffix}`}
          name={`health-filter${suffix}`}
          checked={tempFilters.healthIsEmpty}
          onChange={() =>
            setTempFilters({
              ...tempFilters,
              healthFrom: null,
              healthTo: null,
              healthIsEmpty: true,
              healthHasAnyValue: false,
            })
          }
        />
        <label htmlFor={`filter-health-empty${suffix}`}>{capitalize(i18n.t("js.filters_options.is_empty"))}</label>
      </DropdownMenuItem>
      <li>
        <DropdownMenuSubmit type="button" onMouseDown={(e: React.MouseEvent) => e.preventDefault()} onClick={onApply}>
          {i18n.t("js.apply_filters")}
        </DropdownMenuSubmit>
      </li>
    </>
  )
}

// Checkbox List Panel Component
type CheckboxOption = {
  id: string | number
  label: string
  checked: boolean
}

type CheckboxPanelProps = {
  title: string
  options: CheckboxOption[]
  onToggle: (id: string | number, checked: boolean) => void
  onBack: () => void
  onApply: () => void
  idPrefix: string
  emptyMessage?: string
  hideHeader?: boolean
}

export const CheckboxFilterPanel = ({
  title,
  options,
  onToggle,
  onBack,
  onApply,
  idPrefix,
  emptyMessage,
  hideHeader = false,
}: CheckboxPanelProps) => (
  <>
    {!hideHeader && (
      <DropdownMenuHeader>
        <Button variant="ghost" size="sm" iconOnly type="button" onClick={onBack}>
          <Icon icon="arrow-left" />
        </Button>
        <DropdownMenuTitle>{title}</DropdownMenuTitle>
      </DropdownMenuHeader>
    )}
    {options.length > 0 ? (
      options.map((option) => (
        <DropdownMenuItem key={option.id}>
          <Checkbox
            id={`${idPrefix}-${option.id}`}
            checked={option.checked}
            onChange={(e) => onToggle(option.id, e.target.checked)}
          />
          <label htmlFor={`${idPrefix}-${option.id}`}>{option.label}</label>
        </DropdownMenuItem>
      ))
    ) : emptyMessage ? (
      <div className="empty-space empty-space--sm p-4">
        <p className="empty-space__desc">{emptyMessage}</p>
      </div>
    ) : null}
    <li>
      <DropdownMenuSubmit type="button" onMouseDown={(e: React.MouseEvent) => e.preventDefault()} onClick={onApply}>
        {i18n.t("js.apply_filters")}
      </DropdownMenuSubmit>
    </li>
  </>
)

// Filter Tag Component
type FilterTagProps = {
  label: string
  values: { id: string | number; label: string }[]
  onRemoveValue: (id: string | number) => void
  onClearAll: () => void
  onOpenDropdown: () => void
  isDropdownOpen: boolean
  renderPanel: () => React.ReactNode
  maxVisibleValues?: number
}

export const FilterTag = ({
  label,
  values,
  onRemoveValue,
  onClearAll,
  onOpenDropdown,
  isDropdownOpen,
  renderPanel,
  maxVisibleValues = 2,
}: FilterTagProps) => (
  <span className="h-filter">
    <DropdownMenu>
      <Button size="sm" className="h-filter__button" type="button" onClick={onOpenDropdown}>
        {label}
        <Icon icon="angle-down" aria-hidden="true" />
      </Button>
      {isDropdownOpen && (
        <DropdownMenuContent className="dropdown-menu-filter show">{renderPanel()}</DropdownMenuContent>
      )}
    </DropdownMenu>
    {values.length > maxVisibleValues ? (
      <div className="h-filter__value">
        {values.length} {i18n.t("js.components.filters.checkboxes.options")}
        <button className="btn-empty h-filter__remove" type="button" onClick={onClearAll}>
          <Icon icon="times" aria-hidden="true" size="sm" />
        </button>
      </div>
    ) : (
      values.map((v) => (
        <div key={v.id} className="h-filter__value">
          {v.label}
          <button className="btn-empty h-filter__remove" type="button" onClick={() => onRemoveValue(v.id)}>
            <Icon icon="times" aria-hidden="true" size="sm" />
          </button>
        </div>
      ))
    )}
  </span>
)

// Health Filter Tag (special case with single value display)
type HealthFilterTagProps = {
  filters: HealthFilterState
  onClear: () => void
  onOpenDropdown: () => void
  isDropdownOpen: boolean
  renderPanel: () => React.ReactNode
  labelKey?: string
}

export const HealthFilterTag = ({
  filters,
  onClear,
  onOpenDropdown,
  isDropdownOpen,
  renderPanel,
  labelKey = "js.email_accounts_center.columns.health",
}: HealthFilterTagProps) => {
  const getHealthDisplayValue = () => {
    if (filters.healthIsEmpty) {
      return capitalize(i18n.t("js.filters_options.is_empty"))
    }
    if (filters.healthHasAnyValue) {
      return capitalize(i18n.t("js.filters_options.has_any_value"))
    }
    if (filters.healthFrom !== null && filters.healthTo !== null) {
      return i18n.t("js.filters_options.between", {
        from: filters.healthFrom,
        to: filters.healthTo,
      })
    }
    if (filters.healthFrom !== null) {
      return `${i18n.t("js.filters_options.more_than")} ${filters.healthFrom}`
    }
    return `${i18n.t("js.filters_options.less_than")} ${filters.healthTo}`
  }

  return (
    <span className="h-filter">
      <DropdownMenu style={{ position: "relative", display: "inline-block" }}>
        <Button size="sm" className="h-filter__button" type="button" onClick={onOpenDropdown}>
          {i18n.t(labelKey)}
          <Icon icon="angle-down" aria-hidden="true" />
        </Button>
        {isDropdownOpen && (
          <DropdownMenuContent className="dropdown-menu-filter show">{renderPanel()}</DropdownMenuContent>
        )}
      </DropdownMenu>
      <div className="h-filter__value">
        {getHealthDisplayValue()}
        <button className="btn-empty h-filter__remove" type="button" onClick={onClear}>
          <Icon icon="times" aria-hidden="true" size="sm" />
        </button>
      </div>
    </span>
  )
}
